import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { type ChargeOrRefundDialogViewModel } from './ChargeOrRefundDialogViewModel'
import { ChargeOrRefundDialog } from '@/features/jobs/details/components/ChargeOrRefundDialog'
import { NumericInputContainer } from '@/components/inputs/NumericInputContainer'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { CustomSelect } from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'

export interface ChargeOrRefundDialogContainerProps {
  vm: ChargeOrRefundDialogViewModel
}

export const ChargeOrRefundDialogContainer = observer(
  function ChargeOrRefundDialogContainer({
    vm,
  }: ChargeOrRefundDialogContainerProps) {
    const paymentMethodSelectProps = useCustomSelectStateFromItemSource({
      itemSource: useMemo(
        () =>
          ItemSource.static(vm.availableBillingMethodOptions, (item, text) =>
            item.displayLabel.includes(text.toLowerCase()),
          ),
        [vm.availableBillingMethodOptions],
      ),
    })

    return (
      <ChargeOrRefundDialog
        showing={vm.modal.showing}
        onClose={vm.modal.close}
        mode={vm.mode}
        canCharge={vm.canChargeJob}
        onChangeMode={vm.changeMode}
        billingMethodError={vm.validation.getError('billingMethod')}
        billingMethodSelect={
          <CustomSelect
            {...paymentMethodSelectProps}
            multi={false}
            icon="dropdown"
            itemToString={(i) => String(i?.displayLabel)}
            placeholder="Billing Method"
            value={vm.billingMethod ?? undefined}
            status={
              vm.validation.getError('billingMethod') ? 'danger' : 'default'
            }
            onChange={vm.setBillingMethod}
            testId="custom-select"
          />
        }
        amountInput={
          <NumericInputContainer
            label="Amount"
            placeholder="0.00"
            help={`How much do you want to ${vm.mode}?`}
            error={vm.validation.getError('amount')}
            model={vm}
            field="amount"
            status={vm.validation.getError('amount') ? 'danger' : 'default'}
            testId="charge-or-refund-dialog-amount"
            precision={2}
          />
        }
        reasonInput={
          <TextAreaContainer
            help={`Why are you ${
              vm.mode === 'charge' ? 'charging' : 'refunding'
            } this job`}
            error={vm.validation.getError('reason')}
            model={vm}
            field="reason"
            name="reason"
            label="Reason"
            data-test="charge-or-refund-dialog-reason"
            placeholder="Provide a reason"
          />
        }
        submitError={
          vm.submit.rejected
            ? vm.submit.error || 'Please check your input and try again.'
            : undefined
        }
        onSubmitErrorClose={vm.submit.reset}
        onSubmit={vm.submit}
        isSubmitPending={vm.submit.pending}
        isSubmitDisabled={vm.disabled}
      />
    )
  },
)
